import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BellRing,
  Copy,
  Eye,
  Megaphone,
  Plus,
  RefreshCw,
  Send,
  Smartphone,
  Trash2,
  Users,
} from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  Drawer,
  EmptyState,
  Field,
  Input,
  Loading,
  Modal,
  Progress,
  SearchInput,
  Segmented,
  Select,
  Stat,
  Textarea,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { dateTimeLabel, num, pct, relative, titleCase } from '../lib/format.js';

const AUDIENCES = [
  { value: 'all_lumpers', label: 'All active lumpers' },
  { value: 'radius', label: 'Lumpers near a ZIP' },
  { value: 'tier_pro', label: 'Pro + crew leads' },
  { value: 'available_now', label: 'Available right now' },
  { value: 'specific', label: 'Hand-picked lumpers' },
];

export default function Notifications() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const { data, meta, loading, refresh } = useApi('/notifications', {
    params: { status: statusFilter, q: debounced || undefined, limit: 120 },
  });
  const { data: templates } = useApi('/notifications/templates');
  const [composeOpen, setComposeOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const list = data || [];
  const summary = meta?.summary || {};

  const resend = async (notification) => {
    try {
      await api.post(`/notifications/${notification.id}/duplicate`);
      toast.ok('Copied as a draft', 'Edit it and send when ready');
      refresh();
    } catch (err) {
      toast.err('Could not duplicate', err.message);
    }
  };

  const sendNow = async (notification) => {
    try {
      const payload = await api.post(`/notifications/${notification.id}/send`);
      toast.ok(`Sent to ${payload.data.recipients} lumpers`);
      refresh();
    } catch (err) {
      toast.err('Could not send', err.message);
    }
  };

  return (
    <>
      <PageHead
        title="Send a message"
        subtitle="Tell lumpers about work, rate changes or paperwork, and see who read it."
        icon={Megaphone}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setComposeOpen(true)}>Write a message</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Messages sent" icon={Send} value={num(summary.sent)} tone="green" foot={`${num(summary.drafts)} drafts · ${num(summary.scheduled)} scheduled`} />
        <Stat label="Phones it reached" icon={Users} value={num(summary.reach)} tone="blue" foot="total push deliveries" />
        <Stat
          label="How many opened it"
          icon={Eye}
          value={summary.reach ? Number(((Number(summary.opened) / Number(summary.reach)) * 100).toFixed(1)) : 0}
          unit="%"
          tone="amber"
          foot={`${num(summary.opened)} opened`}
        />
        <Stat label="People who took work" icon={BellRing} value={num(summary.accepted)} tone="violet" foot="responses to work offers" />
      </div>

      <div className="filter-bar">
        <SearchInput value={query} onChange={setQuery} placeholder="Search broadcasts…" style={{ minWidth: 240, maxWidth: 320 }} />
        <Segmented
          options={[
            { value: 'all', label: 'All' },
            { value: 'sent', label: 'Sent' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'draft', label: 'Drafts' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <div className="grow" />
        <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{list.length} broadcasts</span>
      </div>

      {loading && !list.length ? (
        <Loading rows={7} />
      ) : list.length ? (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Message</th>
                  <th className="narrow">Audience</th>
                  <th className="narrow">Status</th>
                  <th className="right">Reach</th>
                  <th className="right">Opened</th>
                  <th className="right">Claimed</th>
                  <th className="narrow">When</th>
                  <th className="narrow right" />
                </tr>
              </thead>
              <tbody>
                {list.map((notification) => (
                  <tr key={notification.id} className="clickable" onClick={() => setSelected(notification)}>
                    <td>
                      <div className="col gap-2">
                        <span className="truncate" style={{ fontWeight: 600 }}>{notification.title}</span>
                        <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{notification.body}</span>
                        {notification.jobCode ? (
                          <span className="dimmer" style={{ fontSize: 10 }}>attached to {notification.jobCode}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="narrow">
                      <Badge tone="muted" size="sm">{titleCase(notification.audience)}</Badge>
                      {notification.audienceRef ? <div className="dimmer" style={{ fontSize: 10 }}>{notification.audienceRef}</div> : null}
                    </td>
                    <td className="narrow">
                      <Badge
                        size="sm"
                        tone={notification.status === 'sent' ? 'green' : notification.status === 'scheduled' ? 'blue' : 'muted'}
                        dot={notification.status === 'scheduled'}
                      >
                        {titleCase(notification.status)}
                      </Badge>
                    </td>
                    <td className="right num">{num(notification.recipients)}</td>
                    <td className="right">
                      <div className="col gap-2" style={{ minWidth: 64 }}>
                        <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>{pct(notification.openRate, 1)}</span>
                        <Progress value={notification.openRate} tone="lime" thin />
                      </div>
                    </td>
                    <td className="right num">{notification.accepted || '—'}</td>
                    <td className="narrow dim">{notification.sentAt ? relative(notification.sentAt) : notification.scheduledAt ? `scheduled ${relative(notification.scheduledAt)}` : relative(notification.createdAt)}</td>
                    <td className="narrow right">
                      <div className="row gap-6 end">
                        {notification.status === 'draft' ? (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              sendNow(notification);
                            }}
                          >
                            Send
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={Copy}
                          onClick={(e) => {
                            e.stopPropagation();
                            resend(notification);
                          }}
                          aria-label="Duplicate"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState icon={Megaphone} title="No messages sent yet">
            Compose a message to fill open work, announce a rate change, or remind lumpers about paperwork.
          </EmptyState>
        </Card>
      )}

      <ComposeModal
        open={composeOpen}
        templates={templates || []}
        onClose={() => setComposeOpen(false)}
        onSent={() => {
          setComposeOpen(false);
          refresh();
        }}
      />

      <DeliveryDrawer notification={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function ComposeModal({ open, onClose, onSent, templates }) {
  const toast = useToast();
  const [form, setForm] = useState({
    title: '',
    body: '',
    audience: 'all_lumpers',
    audienceRef: '',
    actionLabel: 'View job',
    actionUrl: '/app/dispatch',
    scheduledAt: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const { data: preview } = useApi('/notifications/preview', {
    params: { audience: form.audience, audienceRef: form.audienceRef || undefined },
    deps: [form.audience, form.audienceRef],
    skip: !open,
  });

  useEffect(() => {
    if (!open) setForm((prev) => ({ ...prev, title: '', body: '' }));
  }, [open]);

  const submit = async (send) => {
    if (!form.title || !form.body) {
      toast.err('A title and message are required');
      return;
    }
    setBusy(true);
    try {
      const payload = await api.post('/notifications', {
        ...form,
        lumperIds: preview?.sample?.map((s) => s.id),
        send,
        scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : undefined,
      });
      toast.ok(
        send ? 'Message sent' : 'Saved as a draft',
        send ? `Delivered to ${payload.meta?.recipients ?? preview?.count ?? 0} lumpers` : undefined,
      );
      onSent();
    } catch (err) {
      toast.err('Could not send the broadcast', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Write a message"
      subtitle="Push notification to the lumper app"
      footer={
        <>
          <span className="grow dim" style={{ fontSize: 'var(--fs-sm)' }}>
            Reaches <strong className="num">{num(preview?.count || 0)}</strong> lumpers
          </span>
          <Button variant="ghost" onClick={() => submit(false)} disabled={busy}>Save draft</Button>
          <Button variant="primary" icon={Send} loading={busy} onClick={() => submit(true)}>
            {form.scheduledAt ? 'Schedule' : 'Send now'}
          </Button>
        </>
      }
    >
      <div className="col gap-16">
        <div>
          <span className="label">Start from a template</span>
          <div className="wrap-gap mt-8">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                className="chip"
                onClick={() => setForm((prev) => ({ ...prev, title: template.title, body: template.body, actionLabel: template.actionLabel || prev.actionLabel }))}
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>

        <Field label="Title" required>
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Hot load — 38 pallets in 20 min" />
        </Field>
        <Field label="Message" required hint="Keep it under 140 characters so it fits on a lock screen.">
          <Textarea rows={3} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="Coastal Grocers DC needs a crew at door 14. $268 flat, start in 20 minutes." />
        </Field>

        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="Audience">
            <Select value={form.audience} onChange={(e) => set('audience', e.target.value)} options={AUDIENCES} />
          </Field>
          {form.audience === 'radius' ? (
            <Field label="ZIP or metro">
              <Input value={form.audienceRef} onChange={(e) => set('audienceRef', e.target.value)} placeholder="33127" />
            </Field>
          ) : (
            <Field label="Schedule (optional)">
              <Input type="datetime-local" value={form.scheduledAt} onChange={(e) => set('scheduledAt', e.target.value)} />
            </Field>
          )}
        </div>

        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="Button label"><Input value={form.actionLabel} onChange={(e) => set('actionLabel', e.target.value)} /></Field>
          <Field label="Deep link"><Input value={form.actionUrl} onChange={(e) => set('actionUrl', e.target.value)} /></Field>
        </div>

        <div className="tile">
          <div className="row between">
            <span className="label">Recipients</span>
            <Badge tone="amber">{num(preview?.count || 0)} lumpers</Badge>
          </div>
          <div className="avatar-stack mt-8">
            {(preview?.sample || []).map((lumper) => (
              <Avatar key={lumper.id} name={lumper.name} tone={lumper.photoColor || 'amber'} size="sm" />
            ))}
          </div>
          {!preview?.count ? (
            <Callout tone="amber" className="mt-12" icon={Users}>
              No lumpers match that audience right now.
            </Callout>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function DeliveryDrawer({ notification, onClose }) {
  const { data, loading } = useApi(`/notifications/${notification?.id}/detail`, { skip: !notification });
  const deliveries = data?.deliveries || [];

  return (
    <Drawer
      open={Boolean(notification)}
      onClose={onClose}
      size="wide"
      title={notification?.title}
      subtitle={notification ? `${titleCase(notification.audience)} · ${dateTimeLabel(notification.sentAt || notification.createdAt)}` : ''}
      footer={<Button variant="outline" onClick={onClose}>Close</Button>}
    >
      {notification ? (
        <div className="drawer-stack">
          <div className="tile">
            <div className="row gap-10">
              <Smartphone size={15} style={{ color: 'var(--amber)' }} />
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{notification.title}</div>
                <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{notification.body}</div>
                {notification.actionLabel ? (
                  <Badge tone="blue" size="sm" className="mt-8">{notification.actionLabel} → {notification.actionUrl}</Badge>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid grid-3">
            <div className="tile">
              <span className="label">Reach</span>
              <div className="stat-value sm">{num(notification.recipients)}</div>
            </div>
            <div className="tile">
              <span className="label">Opened</span>
              <div className="stat-value sm">{num(notification.opened)}</div>
            </div>
            <div className="tile">
              <span className="label">Claimed</span>
              <div className="stat-value sm">{num(notification.accepted)}</div>
            </div>
          </div>

          <div>
            <div className="label mb-8">Delivery detail</div>
            {loading && !deliveries.length ? (
              <Loading rows={5} />
            ) : deliveries.length ? (
              <div className="col gap-8">
                {deliveries.map((delivery) => (
                  <div key={delivery.id} className={cx('candidate')} style={{ cursor: 'default' }}>
                    <Avatar name={delivery.lumperName} tone={delivery.lumperPhotoColor || 'amber'} size="sm" />
                    <div className="grow truncate">
                      <Link to={`/app/lumpers/${delivery.lumperId}`} style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                        {delivery.lumperName}
                      </Link>
                      <span className="reason truncate">{delivery.lumperCity} · {titleCase(delivery.lumperTier)}</span>
                    </div>
                    <Badge size="sm" tone={delivery.status === 'read' ? 'green' : 'muted'}>{delivery.status}</Badge>
                    <span className="dimmer" style={{ fontSize: 10 }}>
                      {delivery.readAt ? relative(delivery.readAt) : relative(delivery.at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={Users} title="No delivery records">
                Drafts have not been delivered yet.
              </EmptyState>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
